import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Card from '../common/Card';
import Button from '../common/Button';
import Tabs from '../common/Tabs';
import Table from '../common/Table';
import api from '../../services/api';
import { formatCurrency, formatDate } from '../../utils/formatters';
import '../../styles/customer-details.css';

const CustomerDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [debts, setDebts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadCustomerData();
  }, [id]);

  const loadCustomerData = async () => {
    setLoading(true);
    try {
      const customerRes = await api.get(`/customers.php/${id}`);
      const data = customerRes.data;
      setCustomer(data);
      setTransactions(data.recent_transactions || []);
      setDebts(data.active_debts || []);
    } catch (error) {
      console.error('Failed to load customer data:', error);
    } finally {
      setLoading(false);
    }
  };

  const transactionColumns = [
    {
      key: 'invoice_number',
      label: 'Invoice'
    },
    {
      key: 'created_at',
      label: 'Date',
      render: (value) => formatDate(value)
    },
    {
      key: 'grand_total',
      label: 'Amount',
      render: (value) => formatCurrency(value)
    },
    {
      key: 'payment_status',
      label: 'Status',
      render: (value) => (
        <span className={`status-badge ${value}`}>{value}</span>
      )
    }
  ];

  const debtColumns = [
    {
      key: 'invoice_number',
      label: 'Invoice'
    },
    {
      key: 'created_at',
      label: 'Date',
      render: (value) => formatDate(value)
    },
    {
      key: 'original_amount',
      label: 'Original',
      render: (value) => formatCurrency(value)
    },
    {
      key: 'remaining_amount',
      label: 'Remaining',
      render: (value) => formatCurrency(value)
    },
    {
      key: 'due_date',
      label: 'Due Date',
      render: (value) => formatDate(value)
    },
    {
      key: 'status',
      label: 'Status',
      render: (value) => (
        <span className={`status-badge ${value}`}>{value}</span>
      )
    }
  ];

  const tabs = [
    {
      id: 'overview',
      label: 'Overview',
      content: (
        <div className="overview-tab">
          <div className="info-grid">
            <div className="info-item">
              <label>Full Name</label>
              <p>{customer?.first_name} {customer?.last_name}</p>
            </div>
            <div className="info-item">
              <label>Phone</label>
              <p>{customer?.phone}</p>
            </div>
            <div className="info-item">
              <label>Email</label>
              <p>{customer?.email || '-'}</p>
            </div>
            <div className="info-item">
              <label>ID Number</label>
              <p>{customer?.id_number || '-'}</p>
            </div>
            <div className="info-item">
              <label>Address</label>
              <p>{customer?.address || '-'}</p>
            </div>
            <div className="info-item">
              <label>Customer Since</label>
              <p>{formatDate(customer?.created_at)}</p>
            </div>
          </div>

          <div className="stats-grid">
            <div className="stat-card">
              <span className="stat-label">Total Purchases</span>
              <span className="stat-value">{formatCurrency(customer?.total_purchases || 0)}</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">Transactions</span>
              <span className="stat-value">{customer?.total_transactions || 0}</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">Credit Limit</span>
              <span className="stat-value">{formatCurrency(customer?.credit_limit || 0)}</span>
            </div>
            <div className="stat-card highlight">
              <span className="stat-label">Current Balance</span>
              <span className="stat-value">{formatCurrency(customer?.current_balance || 0)}</span>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'transactions',
      label: 'Transactions',
      content: (
        <Table
          columns={transactionColumns}
          data={transactions}
          onRowClick={(row) => navigate(`/sales/${row.id}`)}
        />
      )
    },
    {
      id: 'debts',
      label: 'Debts',
      content: (
        <Table
          columns={debtColumns}
          data={debts}
          onRowClick={(row) => navigate(`/debts/${row.id}`)}
        />
      )
    }
  ];

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="customer-details">
      <div className="details-header">
        <div className="header-left">
          <Button variant="secondary" onClick={() => navigate('/customers')}>
            ← Back
          </Button>
          <h1>{customer?.first_name} {customer?.last_name}</h1>
          <span className="customer-code">{customer?.customer_code}</span>
        </div>
        <div className="header-actions">
          <Button 
            variant="secondary" 
            onClick={() => navigate(`/customers/${id}/edit`)}
            icon="✏️"
          >
            Edit
          </Button>
          {customer?.current_balance > 0 && (
            <Button 
              variant="primary" 
              onClick={() => navigate(`/debts/payment?customer=${id}`)}
              icon="💰"
            >
              Record Payment
            </Button>
          )}
        </div>
      </div>

      <Tabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
    </div>
  );
};

export default CustomerDetails;